import { useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X, Check, RotateCcw } from 'lucide-react';

interface AvatarCropperProps {
  /** Arquivo de imagem selecionado */
  file: File;
  /** Callback com base64 recortado e redimensionado */
  onCropped: (base64: string) => void;
  /** Fechar sem salvar */
  onCancel: () => void;
  /** Tamanho final em px (default 200) */
  outputSize?: number;
}

/**
 * Modal de recorte circular para avatar.
 * Recorta a imagem em proporcao 1:1, redimensiona para outputSize x outputSize
 * e retorna base64 JPEG otimizado (~80%).
 *
 * @internal true
 * @owner alunos
 * @updated 2026-04-23
 */
export function AvatarCropper({ file, onCropped, onCancel, outputSize = 200 }: AvatarCropperProps) {
  const [crop, setCrop] = useState<Crop>();
  const imgRef = useRef<HTMLImageElement>(null);

  // URL sincrona via blob — nao precisa de FileReader/useEffect
  const imgSrc = useMemo(() => URL.createObjectURL(file), [file]);

  /** Centralizar crop 1:1 quando imagem carrega */
  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const cropSize = Math.min(width, height, 300);
    const newCrop = centerCrop(
      makeAspectCrop({ unit: 'px', width: cropSize }, 1, width, height),
      width,
      height,
    );
    setCrop(newCrop);
  }, []);

  /** Gerar canvas recortado e redimensionado */
  const handleConfirm = useCallback(() => {
    const image = imgRef.current;
    if (!image || !crop) return;

    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Escala entre imagem natural e renderizada
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      outputSize,
      outputSize,
    );

    // Exportar como JPEG 80% para reduzir tamanho
    const base64 = canvas.toDataURL('image/jpeg', 0.8);
    onCropped(base64);
  }, [crop, outputSize, onCropped]);

  /** Reset crop para centro */
  const handleReset = useCallback(() => {
    const image = imgRef.current;
    if (!image) return;
    const cropSize = Math.min(image.width, image.height, 300);
    setCrop(centerCrop(
      makeAspectCrop({ unit: 'px', width: cropSize }, 1, image.width, image.height),
      image.width,
      image.height,
    ));
  }, []);

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onCancel}>
      <div style={{ background: 'white', borderRadius: 16, padding: '24px 28px', maxWidth: 480, width: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,.3)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#111827' }}>Recortar Foto</h3>
          <button onClick={onCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Crop Area */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, background: '#f3f4f6', borderRadius: 10, padding: 12, minHeight: 200 }}>
          <ReactCrop
            crop={crop}
            onChange={c => setCrop(c)}
            aspect={1}
            circularCrop
            minWidth={50}
            minHeight={50}
          >
            <img
              ref={imgRef}
              src={imgSrc}
              alt="Recortar"
              onLoad={onImageLoad}
              style={{ maxHeight: 360, maxWidth: '100%', display: 'block' }}
              crossOrigin="anonymous"
            />
          </ReactCrop>
        </div>

        {/* Preview info */}
        <p style={{ textAlign: 'center', fontSize: '0.75rem', color: '#9ca3af', marginBottom: 16 }}>
          Arraste para ajustar a area de recorte. A foto sera redimensionada para {outputSize}x{outputSize}px.
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button onClick={handleReset} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
            <RotateCcw size={14} />Resetar
          </button>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={handleConfirm} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 20px', background: '#1B365D', color: 'white', border: 'none', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer' }}>
            <Check size={14} />Confirmar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
